"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { axiosApiInstance } from "@/libs/axios";
import { API_ENDPOINTS } from "@/constants/api";
import { toast } from "@/utils/customToast";

type Props = {
  paymentId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSent: () => Promise<void> | void;
};

export default function NotifyPaymentDialog({ paymentId, open, onOpenChange, onSent }: Props) {
  const { t } = useTranslation(["finances", "common"]);
  const [isSending, setIsSending] = useState(false);
  const [messageLength, setMessageLength] = useState(0);

  const schema = z.object({
    notification_message: z
      .string()
      .max(
        1000,
        t(
          "finances:paymentsDetails.notifyDialog.messageMaxLength",
          "Message cannot exceed 1000 characters",
        ),
      )
      .optional(),
    payment_destination: z
      .string()
      .max(
        255,
        t(
          "finances:paymentsDetails.notifyDialog.destinationMaxLength",
          "Payment destination cannot exceed 255 characters",
        ),
      )
      .optional(),
  });

  type FormData = z.infer<typeof schema>;

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { notification_message: "", payment_destination: "" },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          form.reset();
          setMessageLength(0);
        }
        onOpenChange(o);
      }}
    >
      <DialogContent className="bg-background/80 h-fit max-h-dvh max-w-2xl border-none p-0 backdrop-blur-xl">
        <ScrollArea className="h-full max-h-dvh">
          <motion.div animate={{ opacity: 1, y: 0 }} className="p-6" initial={{ opacity: 0, y: 20 }} transition={{ duration: 0.3 }}>
            <DialogHeader className="pb-4">
              <DialogTitle className="text-accent-foreground flex items-center gap-3 text-xl font-semibold">
                <div className="bg-muted/20 flex size-10 items-center justify-center rounded-xl backdrop-blur-sm">
                  <Icon className="text-accent-foreground" height="1.25rem" icon="mdi:bell-ring" width="1.25rem" />
                </div>
                {t("finances:paymentsDetails.notifyDialog.title", "Send payment reminder")}
              </DialogTitle>
            </DialogHeader>

            <Form {...form}>
              <form
                className="space-y-6"
                onSubmit={form.handleSubmit(async (data) => {
                  if (!paymentId) return;
                  try {
                    setIsSending(true);
                    const payload: Record<string, string> = {};
                    if (data.notification_message && data.notification_message.trim()) {
                      payload.notification_message = data.notification_message.trim();
                    }
                    if (data.payment_destination && data.payment_destination.trim()) {
                      payload.payment_destination = data.payment_destination.trim();
                    }
                    await axiosApiInstance.post(
                      API_ENDPOINTS.TUTOR.FINANCES.PAYMENT_SEND_REMINDER(paymentId),
                      Object.keys(payload).length ? payload : undefined,
                    );
                    toast.success(t("finances:paymentsDetails.reminderSent", "Payment reminder sent"));
                    await onSent?.();
                    onOpenChange(false);
                    form.reset();
                    setMessageLength(0);
                  } catch (err: any) {
                    if (err?.response?.status === 422 && err?.response?.data?.errors) {
                      const ve = err.response.data.errors;
                      if (ve.notification_message && ve.notification_message[0]) {
                        form.setError("notification_message", { type: "server", message: ve.notification_message[0] });
                      }
                      if (ve.payment_destination && ve.payment_destination[0]) {
                        form.setError("payment_destination", { type: "server", message: ve.payment_destination[0] });
                      }
                    } else {
                      toast.error(
                        err?.response?.data?.message ||
                          t("finances:paymentsDetails.reminderFailed", "Failed to send reminder"),
                      );
                    }
                  } finally {
                    setIsSending(false);
                  }
                })}
              >
                <FormField
                  control={form.control}
                  name="notification_message"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <div className="flex items-center justify-between">
                        <FormLabel className="text-accent-foreground flex items-center gap-2 text-lg font-medium">
                          <Icon height="1.25rem" icon="mdi:text-box" width="1.25rem" />
                          {t("finances:paymentsDetails.notifyDialog.messageLabel", "Message")}
                        </FormLabel>
                        <span className="text-muted-foreground text-sm">{messageLength}/1000</span>
                      </div>
                      <FormControl>
                        <Card className="border-input overflow-hidden rounded-xl border shadow-sm">
                          <div className="p-0">
                            <ScrollArea className="border-input bg-background ring-offset-background focus-within:ring-ring h-32 w-full rounded-xl">
                              <Textarea
                                className="[field-sizing:content] min-h-[128px] resize-none rounded-xl border-none! p-4 text-base break-words whitespace-pre-wrap focus-visible:ring-0 focus-visible:ring-offset-0"
                                placeholder={t(
                                  "finances:paymentsDetails.notifyDialog.messagePlaceholder",
                                  "Optional note to include with the reminder",
                                )}
                                {...field}
                                maxLength={1000}
                                onChange={(e) => {
                                  field.onChange(e.target.value);
                                  setMessageLength(e.target.value.length);
                                }}
                              />
                            </ScrollArea>
                          </div>
                        </Card>
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="payment_destination"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel className="text-accent-foreground flex items-center gap-2 text-lg font-medium">
                        <Icon height="1.25rem" icon="mdi:credit-card-outline" width="1.25rem" />
                        {t("finances:paymentsDetails.notifyDialog.destinationLabel", "Payment destination")}
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t(
                            "finances:paymentsDetails.notifyDialog.destinationPlaceholder",
                            "Where the student should pay (e.g., bank, wallet)",
                          )}
                          {...field}
                          maxLength={255}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    className="bg-muted/20 hover:bg-muted/30 border-border/50 rounded-xl"
                    type="button"
                    variant="outline"
                    onClick={() => {
                      onOpenChange(false);
                      form.reset();
                      setMessageLength(0);
                    }}
                  >
                    {t("common:cancel", "Cancel")}
                  </Button>
                  <Button className="gap-2 rounded-xl" disabled={isSending} type="submit" variant="foreground">
                    {isSending ? (
                      <>
                        <Icon className="animate-spin" height="1rem" icon="lucide:loader-2" width="1rem" />
                        {t("finances:paymentsDetails.notifyDialog.sending", "Sending...")}
                      </>
                    ) : (
                      <>
                        <Icon height="1rem" icon="mdi:bell-ring" width="1rem" />
                        {t("finances:paymentsDetails.notifyDialog.send", "Send reminder")}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          </motion.div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}


